import {
  InvitationStatusEnum,
  PatientUploadStatusEnum,
  QcStatusEnum,
  StudyStatusEnum,
  UserStatusEnum,
  VisitStatusEnum,
} from '@shared/types/status';
import { capitalize } from 'lodash';
import { twMerge } from '@shared/utils/twMerge';
import React, { ReactNode } from 'react';
import { ReadingStatus } from '@shared/types/readings.types';
import { TaskDefinitionExecutionNamedStatus } from '@shared/types/qcTaskDefinitions';
import { ExecutionStatus } from '@shared/types/executions';
import { SubjectStatus } from '@shared/types/subjects';

const colors = {
  light: 'text-spanishGray bg-blueTone dark:bg-royalBrown dark:text-vividTangelo',
  peach: 'text-vividTangelo bg-seashell dark:bg-royalBrown',
  purple: 'text-munsellPurple bg-snow dark:bg-englishViolet dark:text-paleViolet',
  blue: 'text-honoluluBlue bg-waterLight dark:text-water dark:bg-darkCerulean',
  green: 'text-yellowGreen bg-honeydew dark:text-limeGreen dark:bg-deepMossGreen',
  gray: 'text-dimGray bg-ghostWhite dark:text-silverSand dark:bg-arsenic',
  red: 'text-redOrange bg-seashell dark:text-oriolesOrange dark:bg-royalBrown',
  vivid: 'bg-vividTangelo text-white',
} as const;

const darkColors = {
  light: 'bg-royalBrown text-vividTangelo',
  peach: 'bg-royalBrown',
  purple: 'bg-englishViolet text-paleViolet',
  blue: 'text-water bg-darkCerulean',
  green: 'text-limeGreen bg-deepMossGreen',
  gray: 'text-silverSand bg-arsenic',
  red: 'text-oriolesOrange bg-royalBrown',
  vivid: 'bg-vividTangelo text-white',
} as const;

const dottedColors = {
  light: 'bg-spanishGray',
  peach: 'bg-vividTangelo',
  purple: 'bg-munsellPurple',
  blue: 'bg-honoluluBlue',
  green: 'bg-yellowGreen',
  gray: 'bg-dimGray',
  red: 'bg-redOrange',
} as const;
export type statusMapSupportedValues =
  | StudyStatusEnum
  | UserStatusEnum
  | QcStatusEnum
  | PatientUploadStatusEnum
  | ReadingStatus
  | TaskDefinitionExecutionNamedStatus
  | VisitStatusEnum
  | InvitationStatusEnum
  | '';

export const statusMap: Record<statusMapSupportedValues, string> = {
  DRAFT: colors['light'],
  START_UP: colors['peach'],
  MAINTENANCE: colors['purple'],
  CLOSE_UP: colors['blue'],
  COMPLETED: colors['green'],
  CLOSED: colors['green'],
  ON_HOLD: colors['gray'],
  CANCELLED: colors['red'],
  ACTIVE: colors['green'],
  DEACTIVATED: colors['gray'],
  INACTIVE: colors['red'],
  INVITED: colors['light'],
  PENDING: colors['peach'],
  ACCEPTED: colors['green'],
  EXPIRED: colors['gray'],
  REVOKED: colors['red'],
  IN_PROGRESS: colors['blue'],
  QUERY_OPEN: colors['purple'],
  DONE: colors['green'],
  FAILED: colors['red'],
  CREATED: colors['peach'],
  ONGOING: colors['blue'],
  WITHDRAWN: colors['red'],
  READING_PENDING: colors['vivid'],
  TERMINATED: colors['gray'],
  '': '',
};
export const statusMapDark: Record<statusMapSupportedValues, string> = {
  DRAFT: darkColors['light'],
  START_UP: darkColors['peach'],
  MAINTENANCE: darkColors['purple'],
  CLOSE_UP: darkColors['blue'],
  COMPLETED: darkColors['green'],
  CLOSED: darkColors['green'],
  ON_HOLD: darkColors['gray'],
  CANCELLED: darkColors['red'],
  ACTIVE: darkColors['green'],
  DEACTIVATED: darkColors['gray'],
  INACTIVE: darkColors['red'],
  INVITED: darkColors['light'],
  PENDING: darkColors['peach'],
  ACCEPTED: darkColors['green'],
  EXPIRED: darkColors['gray'],
  REVOKED: darkColors['red'],
  IN_PROGRESS: darkColors['blue'],
  QUERY_OPEN: darkColors['purple'],
  DONE: darkColors['green'],
  FAILED: darkColors['red'],
  CREATED: darkColors['peach'],
  ONGOING: darkColors['blue'],
  WITHDRAWN: darkColors['red'],
  READING_PENDING: darkColors['vivid'],
  TERMINATED: darkColors['gray'],
  '': '',
};

const dottedColorsMap: Record<statusMapSupportedValues, string> = {
  PENDING: dottedColors['peach'],
  ACCEPTED: dottedColors['green'],
  EXPIRED: dottedColors['gray'],
  REVOKED: dottedColors['red'],
  IN_PROGRESS: dottedColors['blue'],
  ON_HOLD: dottedColors['gray'],
  QUERY_OPEN: dottedColors['purple'],
  DONE: dottedColors['green'],
  FAILED: dottedColors['red'],
  CREATED: dottedColors['peach'],
  ONGOING: dottedColors['blue'],
  COMPLETED: dottedColors['green'],
  CLOSED: dottedColors['green'],
  WITHDRAWN: dottedColors['red'],
  '': '',
  DRAFT: '',
  START_UP: '',
  MAINTENANCE: '',
  CLOSE_UP: '',
  CANCELLED: '',
  ACTIVE: '',
  DEACTIVATED: '',
  INACTIVE: '',
  INVITED: '',
  READING_PENDING: '',
  TERMINATED: '',
} as const;

type StatusPillProps = {
  status:
    | StudyStatusEnum
    | UserStatusEnum
    | QcStatusEnum
    | PatientUploadStatusEnum
    | ReadingStatus
    | TaskDefinitionExecutionNamedStatus
    | ExecutionStatus
    | VisitStatusEnum
    | SubjectStatus
    | InvitationStatusEnum;
  className?: string;
  dark?: boolean;
  dotted?: boolean;
  textSeparator?: string;
  textCombiner?: string;
  children?: ReactNode;
};

export const StatusPill = ({
  status,
  className,
  dark,
  dotted,
  textSeparator = '_',
  textCombiner = '-',
  children,
}: StatusPillProps) => {
  const colorValue = dotted ? '' : dark ? statusMapDark[status] : statusMap[status];
  const dotColor = dottedColorsMap[status];

  return (
    <div
      className={twMerge(
        colorValue,
        'text-1424 flex items-center whitespace-nowrap rounded-xl px-1.5 py-0.5',
        className
      )}
    >
      {dotted && (
        <span className={twMerge('me-1.5 flex h-1 w-1 flex-shrink-0 rounded-full', dotColor)} />
      )}

      {status
        ?.split(textSeparator)
        ?.map(text => capitalize(text))
        ?.join(textCombiner)}
      {children}
    </div>
  );
};
